import React, { useState, useEffect, useRef } from 'react';
import styles from './Achievement.module.css';
import { apiUrl } from '../config/api';

interface AchievementItem {
  id: number;
  title: string;
  icon: string;
  value: number;
  suffix: string;
  order: number;
  is_active: boolean;
}

const Achievement: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [achievements, setAchievements] = useState<AchievementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counters, setCounters] = useState<{ [key: number]: number }>({});

  const sectionRef = useRef<HTMLDivElement>(null);
  const animationStarted = useRef(false);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await fetch(apiUrl('/api/achievements'));

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setAchievements(result.data);
          // Initialize counters to 0 (for animation)
          const initialCounters: { [key: number]: number } = {};
          result.data.forEach((item: AchievementItem) => {
            initialCounters[item.id] = 0;
          });
          setCounters(initialCounters);
        } else {
          setError('Failed to fetch achievements');
        }
      } catch (err) {
        setError('Failed to load achievements');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAchievements();
  }, []);

  // Intersection Observer for animation
  useEffect(() => {
    if (achievements.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animationStarted.current) {
          animationStarted.current = true;
          setIsVisible(true);
          startCounting();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [achievements]);

  const startCounting = () => {
    achievements.forEach(achievement => {
      const endValue = achievement.value;
      const duration = 800;
      const steps = 60;
      const stepValue = endValue / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(
          Math.floor(stepValue * currentStep),
          endValue
        );

        setCounters(prev => ({
          ...prev,
          [achievement.id]: newValue
        }));

        if (currentStep >= steps) {
          clearInterval(timer);
          setCounters(prev => ({
            ...prev,
            [achievement.id]: endValue
          }));
        }
      }, duration / steps);
    });
  };

  if (loading) {
    return (
      <section className={styles.counter} ref={sectionRef}>
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading achievements...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.counter} ref={sectionRef}>
        <div className={styles.container}>
          <div className={styles.errorState}>
            <p className={styles.errorMessage}>⚠️ {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (achievements.length === 0) {
    return (
      <section className={styles.counter} ref={sectionRef}>
        <div className={styles.container}>
          <div className={styles.emptyState}>
            <p>No achievements available.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className={`${styles.counter} ${isVisible ? styles.visible : ''}`} 
      ref={sectionRef}
    >
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.col12}>
            <div className={styles.counter__inner}>
              {achievements.map((achievement, index) => (
                <React.Fragment key={achievement.id}>
                  <div className={styles.counter__single}>
                    <div className={styles.thumb}>
                      <i className={achievement.icon}></i>
                    </div>
                    <div className={styles.counter__content}>
                      <h2>
                        <span className={styles.odometer}>
                          {counters[achievement.id] ?? 0}
                        </span>
                        <span className={styles.prefix}>
                          {achievement.suffix}
                        </span>
                      </h2>
                      <h5>{achievement.title}</h5>
                    </div>
                  </div>

                  {index < achievements.length - 1 && (
                    <div className={styles.divider}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.poor}></div>
      <div className={styles.shapeLeft}></div>
    </section>
  );
};

export default Achievement;



// working in dynamic well
// import React, { useState, useEffect, useRef } from 'react';
// import styles from './Achievement.module.css';

// interface AchievementItem {
//   id: number;
//   title: string;
//   icon: string;
//   value: number;
//   suffix: string;
//   order: number;
//   is_active: boolean;
// }

// const Achievement: React.FC = () => {
//   const [isVisible, setIsVisible] = useState(false);
//   const [achievements, setAchievements] = useState<AchievementItem[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [counters, setCounters] = useState<{ [key: number]: number }>({});
//   const [hasAnimated, setHasAnimated] = useState(false); // ← ADD THIS

//   const sectionRef = useRef<HTMLDivElement>(null);
//   const animationStarted = useRef(false); // ← ADD THIS

//   // Fetch achievements from API
//   useEffect(() => {
//     const fetchAchievements = async () => {
//       try {
//         console.log('Fetching achievements from API...');
//         const response = await fetch(apiUrl('/api/achievements'));

//         if (!response.ok) {
//           throw new Error(`HTTP error! Status: ${response.status}`);
//         }

//         const result = await response.json();
//         console.log('API Response:', result);

//         if (result.success) {
//           setAchievements(result.data);
//           // Initialize counters with the actual values
//           const initialCounters: { [key: number]: number } = {};
//           result.data.forEach((item: AchievementItem) => {
//             initialCounters[item.id] = item.value; // ← SET TO ACTUAL VALUE, NOT 0!
//           });
//           setCounters(initialCounters);
//           setHasAnimated(true); // ← Mark as animated since values are set
//         } else {
//           setError('API returned unsuccessful response');
//         }
//       } catch (err) {
//         const errorMessage = err instanceof Error ? err.message : 'Unknown error';
//         setError(`Failed to fetch achievements: ${errorMessage}`);
//         console.error('Fetch error:', err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAchievements();
//   }, []);

//   // Intersection Observer for animation
//   useEffect(() => {
//     if (achievements.length === 0 || hasAnimated) return;

//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting && !animationStarted.current) {
//           animationStarted.current = true;
//           setIsVisible(true);
//           startCounting();
//         }
//       },
//       { threshold: 0.001 }
//     );

//     if (sectionRef.current) {
//       observer.observe(sectionRef.current);
//     }

//     return () => {
//       if (sectionRef.current) {
//         observer.unobserve(sectionRef.current);
//       }
//     };
//   }, [achievements]);

//   const startCounting = () => {
//     // Reset counters to 0 before animation
//     const resetCounters: { [key: number]: number } = {};
//     achievements.forEach(achievement => {
//       resetCounters[achievement.id] = 0;
//     });
//     setCounters(resetCounters);

//     // Start counting
//     achievements.forEach(achievement => {
//       const endValue = achievement.value;
//       const duration = 800;
//       const steps = 60;
//       const stepValue = endValue / steps;
//       let currentStep = 0;

//       const timer = setInterval(() => {
//         currentStep++;
//         const newValue = Math.min(
//           Math.floor(stepValue * currentStep),
//           endValue
//         );

//         setCounters(prev => ({
//           ...prev,
//           [achievement.id]: newValue
//         }));

//         if (currentStep >= steps) {
//           clearInterval(timer);
//           setCounters(prev => ({
//             ...prev,
//             [achievement.id]: endValue
//           }));
//         }
//       }, duration / steps);
//     });

//     setHasAnimated(true);
//   };

//   if (loading) {
//     return (
//       <section className={styles.counter} ref={sectionRef}>
//         <div className={styles.container}>
//           <div className={styles.loadingState}>
//             <div className={styles.loadingSpinner}></div>
//             <p>Loading achievements...</p>
//           </div>
//         </div>
//       </section>
//     );
//   }

//   if (error) {
//     return (
//       <section className={styles.counter} ref={sectionRef}>
//         <div className={styles.container}>
//           <div className={styles.errorState}>
//             <p className={styles.errorMessage}>⚠️ {error}</p>
//           </div>
//         </div>
//       </section>
//     );
//   }

//   if (achievements.length === 0) {
//     return (
//       <section className={styles.counter} ref={sectionRef}>
//         <div className={styles.container}>
//           <div className={styles.emptyState}>
//             <p>No achievements available.</p>
//           </div>
//         </div>
//       </section>
//     );
//   }

//   return (
//     <section className={styles.counter} ref={sectionRef}>
//       <div className={styles.container}>
//         <div className={styles.row}>
//           <div className={styles.col12}>
//             <div className={styles.counter__inner}>
//               {achievements.map((achievement, index) => (
//                 <React.Fragment key={achievement.id}>
//                   <div className={styles.counter__single}>
//                     <div className={styles.thumb}>
//                       <i className={achievement.icon}></i>
//                     </div>
//                     <div className={styles.counter__content}>
//                       <h2>
//                         <span className={styles.odometer}>
//                           {counters[achievement.id] ?? 0}
//                         </span>
//                         <span className={styles.prefix}>
//                           {achievement.suffix}
//                         </span>
//                       </h2>
//                       <h5>{achievement.title}</h5>
//                     </div>
//                   </div>

//                   {index < achievements.length - 1 && (
//                     <div className={styles.divider}></div>
//                   )}
//                 </React.Fragment>
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className={styles.poor}></div>
//       <div className={styles.shapeLeft}></div>
//     </section>
//   );
// };

// export default Achievement;





// Full working static
// import React, { useState, useEffect, useRef } from 'react';
// import styles from './Achievement.module.css'; // Changed to .module.css



// const Achievement: React.FC = () => {
//   const [isVisible, setIsVisible] = useState(false);
//   const [counters, setCounters] = useState([
//     { id: 1, currentValue: 0, endValue: 10, suffix: '+', title: 'Team member', icon: 'fas fa-users' },
//     { id: 2, currentValue: 0, endValue: 2, suffix: '+', title: 'Ongoing Projects', icon: 'fas fa-tasks' },
//     { id: 3, currentValue: 0, endValue: 0, suffix: '+', title: 'Completed projects', icon: 'fas fa-check-circle' },
//     { id: 4, currentValue: 0, endValue: 0, suffix: '+', title: 'Winning award', icon: 'fas fa-award' }
//   ]);

//   const sectionRef = useRef<HTMLDivElement>(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting && !isVisible) {
//           setIsVisible(true);
//           startCounting();
//         }
//       },
//       { threshold: 0.001 }
//     );

//     if (sectionRef.current) {
//       observer.observe(sectionRef.current);
//     }

//     return () => {
//       if (sectionRef.current) {
//         observer.unobserve(sectionRef.current);
//       }
//     };
//   }, [isVisible]);

//   const startCounting = () => {
//     counters.forEach(counter => {
//       const duration = 800;
//       const steps = 60;
//       const stepValue = counter.endValue / steps;
//       let currentStep = 0;

//       const timer = setInterval(() => {
//         currentStep++;
//         const newValue = Math.min(
//           Math.floor(stepValue * currentStep),
//           counter.endValue
//         );

//         setCounters(prev => prev.map(c => 
//           c.id === counter.id ? { ...c, currentValue: newValue } : c
//         ));

//         if (currentStep >= steps) {
//           clearInterval(timer);
//           setCounters(prev => prev.map(c => 
//             c.id === counter.id ? { ...c, currentValue: counter.endValue } : c
//           ));
//         }
//       }, duration / steps);
//     });
//   };

//   return (
//     <section className={styles.counter} ref={sectionRef}>
//       <div className={styles.container} >
//         <div className={styles.row}>
//           <div className={styles.col12}>
//             <div className={styles.counter__inner}>
//               <div className={styles.counter__single}>
//                 <div className={styles.thumb}>
//                   <i className="fas fa-users"></i>
//                 </div>
//                 <div className={styles.counter__content}>
//                   <h2>
//                     <span className={styles.odometer}>{counters[0].currentValue}</span>
//                     <span className={styles.prefix}>+</span>
//                   </h2>
//                   <h5>Team member</h5>
//                 </div>
//               </div>
              
//               <div className={styles.divider}></div>

//               <div className={styles.counter__single}>
//                 <div className={styles.thumb}>
//                   <i className="fas fa-tasks"></i>
//                 </div>
//                 <div className={styles.counter__content}>
//                   <h2>
//                     <span className={styles.odometer}>{counters[1].currentValue}</span>
//                     <span className={styles.prefix}>+</span>
//                   </h2>
//                   <h5>Ongoing Projects</h5>
//                 </div>
//               </div>
              
//               <div className={styles.divider}></div>

//               <div className={styles.counter__single}>
//                 <div className={styles.thumb}>
//                   <i className="fas fa-check-circle"></i>
//                 </div>
//                 <div className={styles.counter__content}>
//                   <h2>
//                     <span className={styles.odometer}>{counters[2].currentValue}</span>
//                     <span className={styles.prefix}>+</span>
//                   </h2>
//                   <h5>Completed projects</h5>
//                 </div>
//               </div>
              
//               <div className={styles.divider}></div>

//               <div className={styles.counter__single}>
//                 <div className={styles.thumb}>
//                   <i className="fas fa-award"></i>
//                 </div>
//                 <div className={styles.counter__content}>
//                   <h2>
//                     <span className={styles.odometer}>{counters[3].currentValue}</span>
//                     <span className={styles.prefix}>+</span>
//                   </h2>
//                   <h5>Winning award</h5>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className={styles.poor}></div>
//       <div className={styles.shapeLeft}></div>
//     </section>
//   );
// };

// export default Achievement;